import classNames from "classnames";
import { CheckIcon } from "@icons";
import { TagCloseIcon } from "@design-system/Tag/TagCloseIcon";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import styles from "./ComponentAnatomyView.module.scss";

/**
 * What stands inside the tag on the x-ray plate. The playground passes its own
 * sandbox feature state here, so the schematic always draws the same tag the
 * live stage would show.
 */
export interface TagSchematicContent {
    label: string;
    hasLeftIcon: boolean;
    isNumeric: boolean;
    isRemovable: boolean;
}

const defaultContent: TagSchematicContent = {
    label: "Tag",
    hasLeftIcon: true,
    isNumeric: false,
    isRemovable: true,
};

export const TagSchematic = ({
    bindings,
    highlight,
    content = defaultContent,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    content?: TagSchematicContent;
}) => {
    const read = bindingReader(bindings);
    const radius = read("background", "border-radius");
    const iconSize = read("glyph", "width");
    const xWidth = read("xButton", "width");
    const xHeight = read("xButton", "height");
    const showsIcon = content.hasLeftIcon && iconSize !== undefined;
    const showsX = content.isRemovable && xWidth !== undefined;
    const paddingInline =
        (content.isNumeric
            ? read("background", "padding-inline (numeric)")
            : undefined) ?? read("background", "padding-inline");

    return (
        <span
            className={classNames(styles.schematic, styles.buttonSchematic)}
            style={{ height: read("background", "height") }}
        >
            <Part
                partId="border"
                highlight={highlight}
                className={styles.buttonBorder}
                style={{
                    borderColor: read("border", "border-color"),
                    borderWidth: read("border", "border-width"),
                    borderRadius: radius,
                }}
            />
            <Part
                partId="background"
                highlight={highlight}
                className={styles.buttonBackground}
                style={{
                    backgroundColor: read("background", "background-color"),
                    borderRadius: radius,
                }}
            />
            {/* The real .tag sets color: var(--tag-fg) on the root; the icon
                and the X inherit it, so the label colour is set here too. */}
            <span
                className={styles.buttonContent}
                style={{
                    paddingInline,
                    paddingRight: showsX
                        ? read("background", "padding-right (with X)")
                        : undefined,
                    gap: read("background", "gap"),
                    color: read("label", "color"),
                    justifyContent: content.isNumeric ? "center" : undefined,
                    minWidth: content.isNumeric
                        ? read("background", "min-width")
                        : undefined,
                }}
            >
                {showsIcon && (
                    <Part
                        partId="glyph"
                        highlight={highlight}
                        className={styles.tagSlot}
                        style={{ width: iconSize, height: iconSize }}
                    >
                        <CheckIcon />
                    </Part>
                )}
                <Part
                    partId="label"
                    highlight={highlight}
                    className={styles.buttonLabel}
                    style={{
                        fontSize: read("label", "font-size"),
                        fontWeight: read("label", "font-weight"),
                        lineHeight: read("label", "line-height"),
                        letterSpacing: read("label", "letter-spacing"),
                    }}
                >
                    {content.label}
                </Part>
                {showsX && (
                    <Part
                        partId="xButton"
                        highlight={highlight}
                        className={styles.tagXButton}
                        style={{
                            width: xWidth,
                            height: xHeight,
                            borderRadius: read("xButton", "border-radius"),
                        }}
                    >
                        <TagCloseIcon />
                    </Part>
                )}
            </span>
        </span>
    );
};
